import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { 
  Box, 
  Typography, 
  Button, 
  Radio, 
  RadioGroup, 
  FormControlLabel, 
  Checkbox, 
  Paper,
  Container,
  LinearProgress,
  Card,
  CardContent,
  CardActions,
  Stepper,
  Step,
  StepLabel,
  Fade,
  ThemeProvider,
  createTheme,
  Divider,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const theme = createTheme({
  palette: {
    primary: {
      main: '#3f51b5',
    },
    secondary: {
      main: '#f50057',
    },
  },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif',
  },
});

const TestPage = () => {
  const { courseName } = useParams();
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTest = async () => {
      const testDoc = await getDoc(doc(db, 'tests', courseName));
      if (testDoc.exists()) {
        setTest(testDoc.data());
      }
    };
    fetchTest();
  }, [courseName]);

  const handleSubmit = async () => {
    let correctAnswers = 0;
    const detailedResults = test.questions.map((q, index) => {
      let isCorrect = false;
      if (Array.isArray(answers[index])) {
        const correctOptions = q.answerKey.split(',').map(option => option.trim());
        isCorrect = JSON.stringify(answers[index].sort()) === JSON.stringify(correctOptions.sort());
      } else {
        isCorrect = answers[index] === q.answerKey;
      }
      if (isCorrect) correctAnswers++;
      return {
        question: q.question,
        userAnswer: answers[index] || 'Not answered',
        correctAnswer: q.answerKey,
        isCorrect,
      };
    });

    const totalQuestions = test.questions.length;
    const score = correctAnswers;
    const percentage = ((correctAnswers / totalQuestions) * 100).toFixed(2);

    const user = auth.currentUser;
    const userId = user.uid;
    const userEmail = user.email;

    const resultsData = {
      courseName: courseName,
      userId: userId,
      userEmail: userEmail,
      score: score,
      totalQuestions: totalQuestions,
      percentage: percentage,
      detailedResults: detailedResults,
      timestamp: new Date(),
    };

    await setDoc(doc(db, 'testResults', `${userId}_${courseName}`), resultsData);

    setResults({ 
      score, 
      totalQuestions, 
      percentage,
      detailedResults,
    });

  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  if (!test) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }
  const completionDate = new Date().toLocaleDateString(); // Get the current date as completion date
  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md">
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Test on {courseName}
          </Typography>
          <LinearProgress variant="determinate" value={(activeStep + 1) / test.questions.length * 100} sx={{ mb: 4 }} />
          {!results ? (
            <Fade in={true}>
              <Card elevation={3}>
                <CardContent>
                  <Stepper activeStep={activeStep} alternativeLabel>
                    {test.questions.map((_, index) => (
                      <Step key={index}>
                        <StepLabel>Question {index + 1}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" gutterBottom>
                      {test.questions[activeStep].question}
                    </Typography>
                    {test.questions[activeStep].type === 'radio' ? (
                      <RadioGroup
                        onChange={(e) => setAnswers({ ...answers, [activeStep]: e.target.value })}
                        value={answers[activeStep] || ''}
                      >
                        {test.questions[activeStep].options.map((option, optIndex) => (
                          <FormControlLabel key={optIndex} value={option} control={<Radio />} label={option} />
                        ))}
                      </RadioGroup>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {test.questions[activeStep].options.map((option, optIndex) => (
                          <FormControlLabel
                            key={optIndex}
                            control={
                              <Checkbox
                                checked={(answers[activeStep] || []).includes(option)}
                                onChange={(e) => {
                                  const newAnswers = { ...answers };
                                  if (e.target.checked) {
                                    newAnswers[activeStep] = [...(newAnswers[activeStep] || []), option];
                                  } else {
                                    newAnswers[activeStep] = (newAnswers[activeStep] || []).filter(o => o !== option);
                                  }
                                  setAnswers(newAnswers);
                                }}
                              />
                            }
                            label={option}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
                  <Button
                    onClick={handleBack}
                    disabled={activeStep === 0}
                    variant="outlined"
                  >
                    Back
                  </Button>
                  {activeStep === test.questions.length - 1 ? (
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                      Submit
                    </Button>
                  ) : (
                    <Button onClick={handleNext} variant="contained" color="primary">
                      Next
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Fade>
          ) : (
            <Fade in={true}>
              <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h4" style={{ fontWeight: 'bold' }} gutterBottom>Test Results</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1"sx={{ fontWeight: 'bold' }}>Total Questions: {results.totalQuestions}</Typography>
                  <Typography variant="body1"sx={{ fontWeight: 'bold' }}>Correct Answers: {results.score}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Percentage: {results.percentage}%</Typography>
                  {/*<Typography variant="body1" sx={{ fontWeight: 'bold' }}>Time Spent: {results.timeSpent}</Typography>*/}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <CircularProgress 
                    variant="determinate" 
                    value={parseFloat(results.percentage)} 
                    size={150}
                    thickness={7}
                  />
                  <Typography
                    variant="h4"
                    component="div"
                    style={{
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 100,
                      height: 150,
                      fontWeight: 600,
                    }}
                  >
                    {`${Math.round(parseFloat(results.percentage))}%`}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom style={{ fontWeight: 'bold' }}>Question Breakdown</Typography>
                <List>
                  {results.detailedResults.map((result, index) => (
                    <Accordion key={index}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`panel${index}a-content`}
                        id={`panel${index}a-header`}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          {result.isCorrect ? (
                            <CheckCircleOutlineIcon color="success" sx={{ mr: 1 }} />
                          ) : (
                            <ErrorOutlineIcon color="error" sx={{ mr: 1 }} />
                          )}
                          <Typography>Question {index + 1}</Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" gutterBottom>{result.question}</Typography>
                        <Typography variant="body2" color="textSecondary">Your Answer: {Array.isArray(result.userAnswer) ? result.userAnswer.join(', ') : result.userAnswer}</Typography>
                        <Typography variant="body2" color="textSecondary">Correct Answer: {result.correctAnswer}</Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </List>
                <Box sx={{ mt: 2 }}>
                  {/*<Typography variant="body2">Average Time per Question: {results.averageTimePerQuestion} seconds</Typography>*/}
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setResults(null);
                    setAnswers({});
                    setActiveStep(0);
                  }}
                  sx={{ mt: 2 }}
                >
                  Retake Test
                </Button>
                {results && results.percentage > 75 && (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => navigate(`/course/${courseName}/test/certificate`, { state: { completionDate, courseName } })}
                    sx={{ mt: 2, ml: 2 }}
                  >
                    Get Certificate
                  </Button>
                )}
              </Paper>
            </Fade>
          )}
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default TestPage;